import { VideoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { GetAgendaSlotItemResponse } from '@/hooks/queries/use-get-office-agendas-slots';
import { useDateFnsLocale } from '@/hooks/use-date-fns-locale';
import { capitalizeFirst } from '@/lib/capitalize-first';
import { selectSheetViewModel } from '../sheet.viewmodel';
import { formatRetrocessionRate } from '../slot-sheet/format-retrocession-rate';
import { formatSlotSchedule } from '../slot-sheet/format-slot-schedule';
import { SlotAssistantHoursSection } from '../slot-sheet/slot-assistant-hours-section';
import { SlotSheetHeader } from '../slot-sheet/slot-sheet-header';
import { SlotSheetMetaRow } from '../slot-sheet/slot-sheet-meta-row';
import {
  SlotSheetDefinitionList,
  SlotSheetRow,
  SlotSheetSection,
} from '../slot-sheet/slot-sheet-section';
import {
  SlotSheetActions,
  SlotSheetBody,
  SlotSheetContent,
} from '../slot-sheet/slot-sheet-shell';
import { ViewAgendaSlotEventOccurrence } from '../view-admin-agenda-slot-sheet/view-admin-agenda-slot-sheet.viewmodel';
import {
  selectViewUserAgendaSlotSheetViewModel,
  ViewUserAgendaSlotSheetApi,
} from './view-user-agenda-slot-sheet.viewmodel';

export const ViewUserAgendaSlotSheet = ({
  slot,
  officeId,
  eventOccurrence,
  onAfterBooked,
}: {
  slot: GetAgendaSlotItemResponse;
  officeId: string;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
} & ViewUserAgendaSlotSheetApi) => {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();
  const sheetViewModel = selectSheetViewModel({
    sheetName: 'ViewUserAgendaSlotSheet',
  });

  const viewModel = selectViewUserAgendaSlotSheetViewModel({
    slot,
    officeId,
    eventOccurrence,
    sheetViewModel,
    onAfterBooked,
  });

  const schedule = formatSlotSchedule({
    start: eventOccurrence.start,
    end: eventOccurrence.end,
    allDay: eventOccurrence.allday,
    locale,
  });

  const rate = formatRetrocessionRate(viewModel.information.retrocessionRate);
  const isBookable = !slot.reservations || slot.reservations.length === 0;

  return (
    <Sheet
      open={sheetViewModel.data.show}
      onOpenChange={sheetViewModel.api.onOpenChange}
    >
      <SlotSheetContent size="detail">
        <SlotSheetHeader
          status={viewModel.information.reservationStatus.status}
          title={t(viewModel.title.key, viewModel.title.params)}
          dateLabel={schedule.dateLabel}
          timeLabel={schedule.timeLabel}
        >
          <SlotSheetMetaRow
            id={slot.id}
            updatedAt={slot.updatedAt}
          />
        </SlotSheetHeader>

        <SlotSheetBody>
          <SlotSheetSection title={t('slot-sheet.section.slot')}>
            <SlotSheetDefinitionList>
              <SlotSheetRow label={t('slot-sheet.field.period')}>
                {capitalizeFirst(t(`period-of-day.${slot.periodOfTheDay}`))}
                {schedule.timeLabel && (
                  <span className="text-muted-foreground">
                    {' '}
                    · {schedule.timeLabel}
                  </span>
                )}
              </SlotSheetRow>

              <SlotSheetRow
                label={t('view-user-agenda-slot-sheet.information.slot-type')}
              >
                <span className="flex items-center gap-1.5">
                  {viewModel.information.slotType === 'fullvisio' && (
                    <VideoIcon
                      className="size-3.5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  {t(`agenda-slot.types.${viewModel.information.slotType}`)}
                </span>
              </SlotSheetRow>

              {rate && (
                <SlotSheetRow
                  label={t(
                    'view-user-agenda-slot-sheet.information.retrocession-rate',
                  )}
                >
                  <span className="tabular-nums">
                    {rate.percent}
                    <span className="ml-1 text-muted-foreground">
                      ({rate.ratio})
                    </span>
                  </span>
                </SlotSheetRow>
              )}
            </SlotSheetDefinitionList>
          </SlotSheetSection>

          {/* Assistant hours — shown on the substitute's own AA slots only. The
              legacy Skello fallback stays admin-only: the periods here are
              already gated by selectVisibleAssistantPeriods. */}
          <SlotAssistantHoursSection
            assistantPeriods={viewModel.information.assistantPeriods}
          />
        </SlotSheetBody>

        {isBookable && (
          <SlotSheetActions>
            <Button
              className="w-full"
              onClick={viewModel.bookSlot.api.open}
            >
              {t('view-user-agenda-slot-sheet.btn.book')}
            </Button>
          </SlotSheetActions>
        )}
      </SlotSheetContent>
    </Sheet>
  );
};
